import { useEffect, useMemo, useState } from 'react'
import Modal from './Modal.jsx'
import { DateRangeField } from './DatePicker.jsx'
import Icon from './Icon.jsx'
import HoursField, { HoursMode } from './HoursField.jsx'
import ExistingPeriods from './ExistingPeriods.jsx'
import TimeOffPrivateFields, { privateBody, privateFrom } from './TimeOffPrivateFields.jsx'
import { api } from '../lib/api.js'
import { useAuth } from '../contexts/AuthContext.jsx'
import { daysInRange, formatDate, hoursWindowError, timeWindow, weekOf } from '../lib/dates.js'
import { reasonLabel } from '../../../shared/timeoff.js'

// The most the history endpoint will answer for in one go (routes/history.js). A period
// longer than this is rare enough — a season off — that showing the loaded part is fine.
const MAX_LOOKUP_DAYS = 62

/**
 * Recording that somebody is away.
 *
 * The two presets exist because they are what actually gets typed: somebody phones in sick
 * this morning, or takes the week. Both fill in the same range rather than being separate
 * modes — so the common case is one click, and the uncommon one is still just dragging the
 * range the preset put there.
 */
export default function TimeOffModal({
  staff,
  date,
  weekStart = 1,
  existing = [],
  // This person's bookings from whatever board the caller has loaded — the instant answer
  // for the clash list below, drawn before the full one arrives. It is only the loaded
  // window and only the board being looked at; the effect further down asks the server for
  // the whole period across both boards and replaces it. Callers with nothing loaded (the
  // People page) leave it empty and simply wait the round trip.
  bookings = [],
  jobTypes = [],
  editing = null,
  onSave,
  onDelete,
  onClose,
}) {
  const thisWeek = useMemo(() => weekOf(date, weekStart), [date, weekStart])

  const [from, setFrom] = useState(editing?.from || date)
  const [to, setTo] = useState(editing?.to || date)
  const [note, setNote] = useState(editing?.note || '')
  const [priv, setPriv] = useState(() => privateFrom(editing))
  // Whole day unless the row being edited already has hours. Kept as three values rather
  // than derived from the times, so "part of the day" can be chosen before either time is.
  const [hours, setHours] = useState({
    partial: Boolean(editing?.startTime && editing?.endTime),
    startTime: editing?.startTime || '',
    endTime: editing?.endTime || '',
  })
  const [busy, setBusy] = useState(false)
  const [showExisting, setShowExisting] = useState(false)

  // The same rule the server applies: across a range the end may be earlier in the day than
  // the start, because it is on a later day. See hoursWindowError.
  const hoursInvalid = hoursWindowError(hours, from, to) !== null
  const invalid = !from || !to || to < from || hoursInvalid

  /**
   * The jobs this absence covers.
   *
   * Computed off the dates in the form rather than the row as saved, so dragging the range
   * shows what it is about to run into. Whole day and booked is a contradiction; part of a
   * day and booked is not — somebody booked around a morning appointment is the whole point
   * of a window. That is the same rule the month grid rings in red
   * (components/MonthGrid.jsx), and it lives in both places rather than one because the two
   * screens ask it of different shapes: a saved row there, an unsaved form here.
   */
  const { modeName } = useAuth()
  const jobTypeName = useMemo(() => {
    const byId = new Map(jobTypes.map((j) => [j.id, j.name]))
    return (id) => (id ? byId.get(id) || null : null)
  }, [jobTypes])

  // One shape for both sources. The loaded board's rows carry a job type id; the history
  // endpoint has already turned it into a name and knows which board each job is on.
  const local = useMemo(
    () =>
      bookings.map((a) => ({
        id: a.id,
        date: a.date,
        slot: a.slot,
        label: a.label,
        jobTypeName: jobTypeName(a.jobTypeId),
        mode: null,
        inEffect: true,
      })),
    [bookings, jobTypeName],
  )

  /**
   * The full answer: every booking in the period, on either board, whether or not the
   * caller happened to have those days loaded.
   *
   * Asked of /api/history/person rather than pieced together from what is on screen,
   * because the board only holds the window being looked at and only the mode it is showing —
   * a week off that runs past Friday, or a job on the other board, was invisible here. The
   * fetch follows the dates in the form, so dragging the range re-asks; a short delay keeps
   * that from being one request per keystroke.
   */
  const [remote, setRemote] = useState(null) // null = not (yet) known; [] = known empty
  const tooLong = Boolean(from && to && to >= from && daysInRange(from, to) > MAX_LOOKUP_DAYS)
  useEffect(() => {
    // Somebody who is not crew — a PM, an owner, from the Directory — is never booked on a
    // board, so there is nothing to clash with; and the lookup below only knows crew, so it
    // would 404 and leave the bar on "Checking the board…" for as long as the dialog is open.
    // `fieldStaff` is only ever false from the Directory; the board's staff do not carry it.
    if (staff?.fieldStaff === false) {
      setRemote([])
      return undefined
    }
    setRemote(null)
    if (!staff?.id || !from || !to || to < from || tooLong) return undefined
    let stale = false
    const timer = setTimeout(async () => {
      try {
        const res = await api.get(`/api/history/person/${staff.id}?from=${from}&to=${to}`)
        if (stale) return
        setRemote(
          (res.days || []).flatMap((d) =>
            (d.jobs || []).map((j) => ({
              id: j.id,
              date: d.date,
              slot: j.slot,
              label: j.label,
              jobTypeName: j.jobTypeName,
              mode: j.mode,
              inEffect: j.inEffect,
            })),
          ),
        )
      } catch {
        // Nothing to say that the list drawn from the loaded board is not already saying;
        // it stays, and the hint below carries on describing it.
      }
    }, 250)
    return () => {
      stale = true
      clearTimeout(timer)
    }
  }, [staff?.id, staff?.fieldStaff, from, to, tooLong])

  const covered = useMemo(() => {
    if (!from || !to || to < from) return []
    return (remote || local)
      .filter((a) => a.date >= from && a.date <= to)
      .sort((a, b) => (a.date === b.date ? a.slot - b.slot : a.date < b.date ? -1 : 1))
  }, [remote, local, from, to])

  async function submit(e) {
    e.preventDefault()
    if (invalid || busy) return
    setBusy(true)
    try {
      await onSave({
        fromDate: from,
        toDate: to,
        note: note.trim() || null,
        startTime: hours.partial ? hours.startTime : null,
        endTime: hours.partial ? hours.endTime : null,
        ...privateBody(priv),
      })
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      title={`${editing ? 'Edit time off' : 'Time off'} for ${staff?.name || ''}`}
      onClose={onClose}
      width={460}
    >
      <form onSubmit={submit}>
        <HoursMode {...hours} onChange={setHours} />

        {/* The two shortcuts that get used, sitting beside the label as plain actions rather
            than as a mode. They only fill the field in; the field is what says what will be
            saved, and dragging a different range afterwards is not "leaving" anything.
            "This day", not "Today": opened from a cell, `date` is that cell's day, and
            calling it Today was wrong on every column but one. */}
        <DateRangeField
          label="Dates"
          from={from}
          to={to}
          onChange={(f, t) => { setFrom(f); setTo(t) }}
          weekStart={weekStart}
          today={date}
          presets={[
            { label: 'This day', from: date, to: date },
            { label: 'This week', from: thisWeek.from, to: thisWeek.to },
          ]}
        />

        <HoursField {...hours} onChange={setHours} from={from} to={to} />

        <div className="field">
          <label htmlFor="to-note">Note <span className="hint">optional, shown on the board</span></label>
          <input
            id="to-note"
            type="text"
            maxLength={120}
            value={note}
            placeholder="Training, appointment…"
            onChange={(e) => setNote(e.target.value)}
          />
        </div>

        <TimeOffPrivateFields value={priv} onChange={(p) => setPriv((v) => ({ ...v, ...p }))} />

        {/* The DATES, not `invalid` — which is also true when only the hours are backwards,
            and then said so in a sentence about dates while the hours field said the right
            thing two lines above it. */}
        {to < from && <div className="notice">The end date is before the start date.</div>}

        {/* Only when booking new leave, and only when asked for. While editing one period,
            listing the others is a distraction from the one thing this dialog is about; while
            adding one, the other periods are worth a look but are not the question — so they
            sit behind a word rather than taking a block of the form every time. */}
        {!editing && existing.length > 0 && (
          <div className="already-off">
            <button
              type="button"
              className="btn ghost sm already-off-toggle"
              aria-expanded={showExisting}
              onClick={() => setShowExisting((v) => !v)}
            >
              {showExisting ? 'Hide already booked off' : `Show already booked off (${existing.length})`}
            </button>
            {showExisting && (
              <ExistingPeriods
                periods={existing}
                detail={(t) => [timeWindow(t), t.note, reasonLabel(t.reason)].filter(Boolean).join(' · ') || null}
                onRemove={onDelete}
              />
            )}
          </div>
        )}

        <ConflictBar
          checking={remote === null && !tooLong}
          jobs={covered}
          potential={hours.partial}
          modeName={modeName}
          truncated={tooLong ? MAX_LOOKUP_DAYS : 0}
        />

        <div className="modal-actions">
          {editing && (
            <button
              type="button"
              className="btn ghost danger"
              onClick={() => onDelete(editing.id)}
              disabled={busy}
            >
              <Icon name="trash" size={14} />
              Delete
            </button>
          )}
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn primary" disabled={invalid || busy}>
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Add time off'}
          </button>
        </div>
      </form>
    </Modal>
  )
}

/**
 * Whether this absence runs into anything, always on screen.
 *
 * It used to appear only when there was something to report, which meant an empty space had
 * two meanings — nothing clashes, and nobody has looked yet — and they are not the same
 * answer. One line, always there, saying which of the three it is.
 *
 * A whole day off on a day somebody is already scheduled is a contradiction, and wears amber.
 * A window only might be: booking around a morning appointment is the point of one, and the
 * board holds no job times to settle it either way, so that reads as potential and wears
 * yellow. The jobs themselves are behind a disclosure — the count is the answer, the list is
 * what you open when the count is not what you expected.
 */
function ConflictBar({ checking, jobs, potential, modeName, truncated }) {
  const [open, setOpen] = useState(false)
  const count = jobs.length
  // Anything already found outranks the fetch still being in flight: the loaded board answers
  // first and the server only ever adds to it, so "checking" beside a count of one was the
  // bar contradicting itself for a few hundred milliseconds. Checking only means "none yet,
  // and not finished looking" — which is the one case that must not read as all clear.
  const state = count > 0 ? (potential ? 'potential' : 'conflict') : checking ? 'checking' : 'clear'
  const label = {
    checking: 'Checking the board…',
    clear: 'No conflict detected',
    potential: 'Potential scheduling conflict',
    conflict: 'Scheduling conflict detected',
  }[state]

  const summary = count > 0 ? `${count} ${count === 1 ? 'job' : 'jobs'} in this period` : null
  const expandable = count > 0

  return (
    <div className={`conflict-bar ${state} ${open ? 'open' : ''}`}>
      <button
        type="button"
        className="conflict-bar-row"
        disabled={!expandable}
        aria-expanded={expandable ? open : undefined}
        onClick={() => expandable && setOpen((o) => !o)}
      >
        <Icon name={state === 'clear' ? 'checkCircle' : 'badgeAlert'} size={16} strokeWidth={2} />
        <span className="conflict-bar-label">{label}</span>
        {summary && <span className="conflict-bar-count">{summary}</span>}
        {expandable && <Icon name={open ? 'up' : 'down'} size={14} strokeWidth={2.2} />}
      </button>

      {open && (
        <ul className="conflict-bar-jobs">
          {jobs.map((a) => (
            <li key={a.id}>
              <span className="when">{formatDate(a.date)}</span>
              <span className="what">{a.label}</span>
              {a.jobTypeName && <span className="chip">{a.jobTypeName}</span>}
              {/* A job on the board that is NOT running that day is still somebody's plan for
                  this person — worth seeing, but marked, so it is not mistaken for the day's
                  actual work. */}
              {!a.inEffect && a.mode && (
                <span className="board" title="On the board that is not in effect that day">
                  {modeName(a.mode)} board
                </span>
              )}
            </li>
          ))}
          {truncated > 0 && (
            <li className="conflict-bar-more">
              Only the first {truncated} days of this period were checked.
            </li>
          )}
        </ul>
      )}
    </div>
  )
}

